import { useState } from 'react'
import { Copy, KeyRound, Plus, ShieldCheck } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useApiClients, useIntegrationMutations, useMaxPageSize, useOfferableScopes,
} from '../api'
import type { ClientSecretView } from '../types'

/**
 * The systems allowed to read this one.
 *
 * <p>Three things this screen states rather than implies: the secret exists once, a disabled
 * client keeps working until its current token expires, and the scopes are the same permissions
 * a person would hold — so granting one is the same decision, in the same words.
 */
export function ApiClientsPage() {
  useBreadcrumb()
  const clients = useApiClients()
  const scopes = useOfferableScopes()
  const maxPage = useMaxPageSize()
  const actions = useIntegrationMutations()

  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState<{
    name: string; description: string; scopes: string[]; rateLimitPerMinute: string
  } | null>(null)
  const [issued, setIssued] = useState<ClientSecretView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<string | null>(null)

  const term = search.trim().toLowerCase()
  const rows = (clients.data ?? []).filter((client) =>
    !term || client.name.toLowerCase().includes(term) || client.clientId.includes(term))

  const run = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setBanner(null)
      try {
        await work()
      } catch (caught) {
        setBanner(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="API clients"
        subtitle="What another system may read, and how much of it at a time."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({ name: '', description: '', scopes: [], rateLimitPerMinute: '120' })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New client
          </Button>
        }
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A client exchanges its credentials for a token that lasts fifteen minutes, then reads
        through <code>/api/integration/v1</code> exactly as its scopes allow. The API is
        read-only in this release, so only read permissions are offered. A page returns at most{' '}
        {maxPage.data ?? '…'} rows and continues with the cursor it hands back.
      </p>

      <FilterBar
        search={
          <Input
            aria-label="Search clients"
            placeholder="Search by name or client id"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        }
      />

      <Card>
        {clients.isPending && <Skeleton className="m-4 h-24" />}
        {clients.isError && <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />}
        {clients.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nothing is connected"
            description="No system reads this one yet. A client is how a payroll bureau, an ERP or a partner's nightly sync gets in — with the scopes you choose and nothing else."
          />
        )}
        {clients.isSuccess && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">Client</th>
                  <th className="p-3 text-start font-medium">Scopes</th>
                  <th className="p-3 text-start font-medium">Limit</th>
                  <th className="p-3 text-start font-medium">Last used</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((client) => (
                  <tr key={client.id} className="border-b border-[var(--color-border)] align-top">
                    <td className="p-3">
                      <p className="font-medium text-[var(--color-heading)]">{client.name}</p>
                      <p className="font-mono text-xs text-[var(--color-muted)]">{client.clientId}</p>
                      {client.description && (
                        <p className="text-xs text-[var(--color-muted)]">{client.description}</p>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex max-w-xs flex-wrap gap-1">
                        {client.scopes.map((scope) => (
                          <span key={scope} className="rounded bg-[var(--color-page)] px-1.5 py-0.5 font-mono text-xs">
                            {scope}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3">{client.rateLimitPerMinute}/min</td>
                    <td className="p-3">
                      {client.lastUsedAt
                        ? <DateValue value={client.lastUsedAt} withTime />
                        : <span className="text-[var(--color-muted)]">Never</span>}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={client.status}
                        tone={client.status === 'ACTIVE' ? 'success' : 'neutral'}
                        label={client.status === 'ACTIVE' ? 'Active' : 'Disabled'}
                      />
                      {client.usableNote && (
                        <p className="mt-1 max-w-xs text-xs text-[var(--color-muted)]">
                          {client.usableNote}
                        </p>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => run(
                            async () => setIssued(await actions.rotateClient.mutateAsync(client.id)),
                            'That secret could not be rotated')}
                        >
                          <KeyRound className="size-4" aria-hidden="true" />
                          Rotate
                        </Button>
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => run(
                            () => actions.setClientStatus.mutateAsync({
                              id: client.id,
                              status: client.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
                              reason: client.status === 'ACTIVE' ? 'Disabled from the settings screen' : null,
                            }),
                            'That client could not be changed')}
                        >
                          {client.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        title="New API client"
        submitLabel="Create"
        loading={actions.createClient.isPending}
        error={error}
        size="lg"
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              const created = await actions.createClient.mutateAsync({
                name: draft.name,
                description: draft.description || null,
                scopes: draft.scopes,
                rateLimitPerMinute: Number(draft.rateLimitPerMinute),
              })
              setDraft(null)
              setIssued(created)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That client could not be created')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3">
            <div>
              <Label htmlFor="client-name">Name</Label>
              <Input
                id="client-name" value={draft.name}
                placeholder="Payroll bureau nightly sync"
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="client-description">What it is for</Label>
              <Textarea
                id="client-description" rows={2} value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="client-rate">Requests a minute</Label>
              <Input
                id="client-rate" type="number" min={1} value={draft.rateLimitPerMinute}
                onChange={(event) => setDraft({ ...draft, rateLimitPerMinute: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                There is no default: the right number depends on what this client does. Over it,
                requests are refused with a 429 and the seconds to wait.
              </p>
            </div>
            <div>
              <Label>Scopes</Label>
              <p className="mb-2 text-xs text-[var(--color-muted)]">
                The same permissions a person holds. Give it only what it reads.
              </p>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-[var(--color-border)] p-2">
                {scopes.data?.map((scope) => (
                  <label key={scope} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4"
                      aria-label={scope}
                      checked={draft.scopes.includes(scope)}
                      onChange={(event) => setDraft({
                        ...draft,
                        scopes: event.target.checked
                          ? [...draft.scopes, scope]
                          : draft.scopes.filter((held) => held !== scope),
                      })}
                    />
                    <span className="font-mono text-xs">{scope}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={issued != null}
        onOpenChange={(open) => { if (!open) setIssued(null) }}
        title="The secret, once"
        submitLabel="Done"
        onSubmit={() => setIssued(null)}
      >
        {issued && (
          <div className="space-y-3 text-sm">
            <div>
              <Label>Client id</Label>
              <code className="mt-1 block break-all rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-2 font-mono text-xs">
                {issued.clientId}
              </code>
            </div>
            <div>
              <Label>Secret</Label>
              <code className="mt-1 block break-all rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-2 font-mono text-xs">
                {issued.secret}
              </code>
            </div>
            <p className="flex items-start gap-2 text-[var(--color-body)]">
              <Copy className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              {issued.note}
            </p>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
